import '../health-professional-modal.scss';

import { useLazyQuery, useMutation } from '@apollo/client';
import { isValidPhoneNumber } from 'libphonenumber-js/core';
import metadata from 'libphonenumber-js/metadata.min.json';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import { useHealthProfessionalHonorificValues } from '../../../../data/data/health-professionals/select-values';
import { useHealthProfessionalHealthProviderAgendas } from '../../../../data/data/health-professionals/use-health-professional-health-provider-agendas';
import { useGenderValues } from '../../../../data/data/select-values';
import { toUpload } from '../../../../data/GraphQL/api/format';
import {
  ConsultType,
  CreateHealthProfessionalItemDocument,
  Gender,
  GetEmailExistsDocument,
  GetHealthProfessionalItemsDocument,
  HealthProfessionalItemDataFragment,
  Honorific,
  Language,
} from '../../../../data/types/api/graphql';
import { maxBirthDate } from '../../../../data/utils/date.utils';
import { regExEmail } from '../../../../data/utils/form.utils';
import {
  FileSizeImage,
  FileTypeImage,
} from '../../../../data/utils/upload.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import { AvatarUploadController } from '../../../../elements/avatar/avatar-upload/controllers/avatar-upload';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputCheckboxController } from '../../../../elements/form/controllers/input-checkbox';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import { InputSelectController } from '../../../../elements/form/controllers/input-select';
import { FormColumns, FormRow } from '../../../../elements/form/form';
import { BaseInputIcon } from '../../../../elements/form/input/base-input';
import { InputCheckboxType } from '../../../../elements/form/input/input-checkbox';
import { InputSelect } from '../../../../elements/form/input/input-select';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Message, MessageType } from '../../../../elements/message/message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { Spinner } from '../../../../elements/spinner/spinner';
import { Tooltip } from '../../../../elements/tooltip/tooltip';
import { useHealthProfessionalForm } from '../../form/use-health-professional-form';

type HealthProfessionalCreateModalProps = {
  onSaved?: (value: HealthProfessionalItemDataFragment) => void;
  onCancel?: () => void;
  onClose?: () => void;
};

export function HealthProfessionalCreateModal({
  onSaved,
  onCancel,
  onClose,
}: HealthProfessionalCreateModalProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const [formStep, setFormStep] = useState<number>(1);

  const [emailCheck, setEmailCheck] = useState<{
    valid: boolean;
    exists: boolean;
  }>({ valid: false, exists: false });

  const onHandleFormStep = useCallback((nextStep: number) => {
    setFormStep(nextStep);
  }, []);

  const { form, formIsValid, setErrors } = useHealthProfessionalForm();

  const [getEmailExists] = useLazyQuery(GetEmailExistsDocument, {
    fetchPolicy: 'network-only',
  });

  const { data: genderData } = useGenderValues();
  // const { data: healthProviderItemsData } = useHealthProviderItems();
  const { data: honorificData } = useHealthProfessionalHonorificValues();

  const healthProviderId = form.watch('healthProviderId');

  const firstName = form.watch('firstName');
  const gender = form.watch('gender');
  const lastName = form.watch('lastName');
  const initials = form.watch('initials');
  const emailAddress = form.watch('email');
  const birthDate = form.watch('birthDate');
  const phoneNumber = form.watch('phoneNumber');
  const selectedHealthProviderId = form.watch('healthProviderId');
  const honorific = form.watch('honorific');

  const {
    loading: practicesLoading,
    practices,
    update: updatePractice,
  } = useHealthProfessionalHealthProviderAgendas(
    undefined,
    false,
    healthProviderId
  );

  const [
    createHealthProfessionalItem,
    { loading: createHealthProfessionalItemLoading },
  ] = useMutation(CreateHealthProfessionalItemDocument, {
    refetchQueries: [GetHealthProfessionalItemsDocument],
  });

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();

      try {
        const result = await createHealthProfessionalItem({
          variables: {
            ...values,
            healthProviderId: healthProviderId ?? '',
            avatar: values.avatar
              ? {
                  fileName: values.avatar.name,
                  base64Data: toUpload(values.avatar.base64),
                }
              : {
                  deleteFile: true,
                },

            healthProviders: practices.map(option => ({
              healthProviderId: option.id,
              patientWeeksBefore: option.value,
            })),
            consultTypes: values.consultTypes.map(
              option => option as unknown as ConsultType
            ),
            isAdmin: values.isAdmin === true,
            language: Language.Nl,
          },
        });

        if (result.data?.healthProfessionalCreate.healthProfessional) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'health-professional.health-professional-create.success',
            }),
          });

          onSaved?.(result.data.healthProfessionalCreate.healthProfessional);

          onClose?.();
        } else {
          setErrors(result.data?.healthProfessionalCreate?.errors);
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    form,
    createHealthProfessionalItem,
    healthProviderId,
    practices,
    showAlertMessage,
    intl,
    onSaved,
    onClose,
    setErrors,
  ]);

  useEffect(() => {
    (async () => {
      if (emailAddress !== '') {
        let valid = regExEmail.test(emailAddress);
        const { data } = await getEmailExists({
          variables: { emailAddress },
        });
        const exists = data?.emailExists ?? false;
        if (exists) {
          valid = false;
        }
        setEmailCheck({ valid, exists });
      }
    })();
  }, [emailAddress, getEmailExists]);

  const canDoNext = useMemo(() => {
    if (
      formStep === 1 &&
      initials &&
      firstName &&
      lastName &&
      birthDate &&
      phoneNumber &&
      emailAddress
    ) {
      const validPhone = isValidPhoneNumber(phoneNumber, metadata);
      if (emailCheck.valid && validPhone && gender) {
        return true;
      }

      return false;
    }
    if (formStep === 2 && honorific && selectedHealthProviderId) {
      return true;
    }

    return false;
  }, [
    birthDate,
    emailAddress,
    emailCheck.valid,
    firstName,
    formStep,
    gender,
    honorific,
    initials,
    lastName,
    phoneNumber,
    selectedHealthProviderId,
  ]);

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (!confirmed && form.formState.isDirty) {
        setCancelPromptOpen(true);
      } else {
        onCancel?.();

        onClose?.();
      }
    },
    [form.formState.isDirty, onCancel, onClose]
  );

  return (
    <>
      <Modal
        testId="health-professional-create"
        title={
          <CustomFormattedMessage id="health-professional.health-professional-create.title" />
        }
        width={ModalWidth.LARGE}
        onClose={onHandleCancel}
        buttons={
          <>
            <Button
              type={ButtonType.SECONDARY}
              inverted
              onClick={() => onHandleCancel(false)}
            >
              <CustomFormattedMessage id="common.cancel" />
            </Button>

            {formStep > 1 && (
              <Button inverted onClick={() => onHandleFormStep(formStep - 1)}>
                <CustomFormattedMessage id="common.previous" />
              </Button>
            )}

            {formStep < 2 ? (
              <Button
                disabled={!canDoNext}
                onClick={() => onHandleFormStep(formStep + 1)}
              >
                <CustomFormattedMessage id="common.next" />
              </Button>
            ) : (
              <Button
                icon={
                  createHealthProfessionalItemLoading
                    ? ButtonIcon.LOADING
                    : ButtonIcon.NONE
                }
                disabled={
                  !canDoNext ||
                  !formIsValid ||
                  createHealthProfessionalItemLoading
                }
                onClick={onHandleSubmit}
              >
                <CustomFormattedMessage id="health-professional.health-professional-create.save" />
              </Button>
            )}
          </>
        }
      >
        <FormProvider {...form}>
          {formStep === 1 && (
            <>
              <FormColumns>
                <FormRow width="100px">
                  <AvatarUploadController
                    name="avatar"
                    fileTypes={FileTypeImage}
                    fileSize={FileSizeImage}
                  />
                </FormRow>

                <FormRow>
                  <InputFieldController
                    name="firstName"
                    label={
                      <>
                        <CustomFormattedMessage id="common.firstname" />
                        <small>*</small>
                      </>
                    }
                    placeholder={intl.formatMessage({ id: 'common.firstname' })}
                  />
                </FormRow>

                <FormRow>
                  <InputFieldController
                    name="lastName"
                    label={
                      <>
                        <CustomFormattedMessage id="common.lastname" />
                        <small>*</small>
                      </>
                    }
                    placeholder={intl.formatMessage({ id: 'common.lastname' })}
                  />
                </FormRow>

                <FormRow>
                  <InputFieldController
                    name="initials"
                    label={
                      <>
                        <CustomFormattedMessage id="common.initials" />
                        <small>*</small>
                      </>
                    }
                    placeholder={intl.formatMessage({ id: 'common.initials' })}
                  />
                </FormRow>
              </FormColumns>

              <FormRow>
                <InputFieldController
                  name="email"
                  label={
                    <>
                      <CustomFormattedMessage id="common.email" />
                      <small>*</small>
                    </>
                  }
                  placeholder={intl.formatMessage({ id: 'common.email' })}
                />
                {emailCheck.exists && (
                  <Message
                    type={MessageType.WARNING}
                    message={
                      <CustomFormattedMessage id="common.errors.email-already-used" />
                    }
                  />
                )}
              </FormRow>

              <FormRow>
                <InputCheckboxController<Gender>
                  type={InputCheckboxType.RADIO}
                  name="gender"
                  label={
                    <>
                      <CustomFormattedMessage id="common.gender" />
                      <small>*</small>
                    </>
                  }
                  options={genderData}
                />
              </FormRow>

              <FormRow>
                <InputFieldController
                  icon={BaseInputIcon.DATE}
                  type="date"
                  name="birthDate"
                  label={
                    <>
                      <CustomFormattedMessage id="common.birthdate" />
                      <small>*</small>
                    </>
                  }
                  max={maxBirthDate}
                />
              </FormRow>

              <FormRow>
                <InputFieldController
                  type="phone"
                  name="phoneNumber"
                  label={
                    <>
                      <CustomFormattedMessage id="common.phone" />
                      <small>*</small>
                    </>
                  }
                  placeholder={intl.formatMessage({
                    id: 'common.phone',
                  })}
                />
              </FormRow>
            </>
          )}

          {formStep === 2 && (
            <>
              <FormRow>
                <InputSelectController<string>
                  name="healthProviderId"
                  label={
                    <>
                      <CustomFormattedMessage id="health-professional.posting" />
                      <small>*</small>
                    </>
                  }
                  placeholder={intl.formatMessage({
                    id: 'common.practice-placeholder',
                  })}
                  options={practices.map(option => ({
                    key: option.id,
                    title: option.name,
                  }))}
                />
              </FormRow>

              <FormRow>
                <InputSelectController<Honorific>
                  name="honorific"
                  label={
                    <>
                      <CustomFormattedMessage id="common.honorific" />
                      <small>*</small>
                    </>
                  }
                  placeholder={intl.formatMessage({
                    id: 'common.honorific-placeholder',
                  })}
                  options={honorificData}
                />
              </FormRow>

              <FormRow>
                <InputFieldController
                  name="meetingRoomLink"
                  label={<CustomFormattedMessage id="common.video-link" />}
                  placeholder={intl.formatMessage({
                    id: 'common.video-link',
                  })}
                />
              </FormRow>

              <FormRow>
                <InputCheckboxController<boolean>
                  name="isAdmin"
                  label={
                    <CustomFormattedMessage id="health-professional.is-admin" />
                  }
                  options={[
                    {
                      key: true,
                      title: (
                        <CustomFormattedMessage id="health-professional.is-admin-placeholder" />
                      ),
                    },
                  ]}
                />
              </FormRow>

              {practices.length > 0 && (
                <FormRow>
                  <div className="health-professional-agenda-table">
                    <div className="health-professional-agenda-table__header">
                      <div className="health-professional-agenda-table-item">
                        <div className="health-professional-agenda-table-item__column">
                          <p>
                            <CustomFormattedMessage id="health-professional.agendas.agenda" />
                          </p>
                        </div>
                        <div className="health-professional-agenda-table-item__column">
                          <p>
                            <CustomFormattedMessage id="health-professional.agendas.public" />
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="health-professional-agenda-table__content">
                      {practices.map(item => (
                        <div
                          key={item.id}
                          className="health-professional-agenda-table-item"
                        >
                          <div className="health-professional-agenda-table-item__tooltip">
                            {item.reasons.length > 0 && (
                              <Tooltip
                                message={
                                  <>
                                    {item.reasons.map(reason => (
                                      <span key={reason}>{reason}</span>
                                    ))}
                                  </>
                                }
                              >
                                <i className="icon info" />
                              </Tooltip>
                            )}
                          </div>
                          <div className="health-professional-agenda-table-item__column">
                            {item.name}
                          </div>

                          {practicesLoading ? (
                            <div className="health-professional-agenda-table-item__loader">
                              <Spinner />
                            </div>
                          ) : (
                            <div className="health-professional-agenda-table-item__column">
                              <InputSelect<string>
                                name="visible"
                                options={[
                                  ...item.options.map(x => ({
                                    key: x.key,
                                    title: intl.formatMessage({
                                      id: x.title,
                                    }),
                                    disabled: x.disabled,
                                  })),
                                  ...Array.from(Array(52).keys()).map(
                                    option => ({
                                      key: `${option + 1}`,
                                      title: intl.formatMessage(
                                        {
                                          id:
                                            option + 1 === 1
                                              ? 'health-professional.agendas.week'
                                              : 'health-professional.agendas.weeks',
                                        },
                                        { count: option + 1 }
                                      ),
                                    })
                                  ),
                                ]}
                                value={`${item.value}`}
                                onChange={val => {
                                  updatePractice(item.id, val);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                      {/* healthProviderItemsData.map(value => {
                        const agenda = agendas.find(
                          option => option.id === value.id
                        );

                        return (
                          <div
                            key={value.id}
                            className="health-professional-agenda-table-item"
                          >
                            <div className="health-professional-agenda-table-item__column">
                              <InputCheckbox<string>
                                name="agenda"
                                options={[{ key: value.id, title: value.name }]}
                                value={agenda ? value.id : ''}
                                onChange={() => onHandleAgenda(value.id)}
                                disabled={value.id === healthProviderId}
                              />
                            </div>
                            <div className="health-professional-agenda-table-item__column">
                              <InputSelect<string>
                                name="visible"
                                options={[
                                  ...[
                                    {
                                      key: '0',
                                      title: (
                                        <CustomFormattedMessage id="health-professional.agendas.not-enabled" />
                                      ),
                                    },
                                  ],
                                  ...Array.from(Array(52).keys()).map(
                                    option => ({
                                      key: `${option + 1}`,
                                      title: intl.formatMessage(
                                        {
                                          id: 'health-professional.agendas.week',
                                        },
                                        { count: option + 1 }
                                      ),
                                    })
                                  ),
                                ]}
                                value={agenda?.visible ?? '0'}
                                disabled={!agenda}
                                onChange={val =>
                                  onHandleAgendaVisibilty(value.id, val!)
                                }
                              />
                            </div>
                          </div>
                        );
                      }) */}
                    </div>
                  </div>
                </FormRow>
              )}
            </>
          )}
        </FormProvider>
      </Modal>

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}
